/**
 * Audio Helper Utility
 * Provides robust audio playback with error handling, promise handling,
 * and support for multiple simultaneous playback during rapid scanning
 */

/**
 * Plays a sound by className with proper error handling
 * @param {string} className - The class name of the audio element
 * @param {number} instanceIndex - Which instance to play (default 0 for first)
 * @returns {Promise<void>}
 */
export const playSoundByClass = (className, instanceIndex = 0) => {
  return new Promise((resolve, reject) => {
    try {
      const audioElements = document.getElementsByClassName(className);
      
      // Check if element exists
      if (!audioElements || audioElements.length === 0) {
        const error = new Error(`Audio element with class '${className}' not found`);
        console.warn(error.message);
        reject(error);
        return;
      }

      // Use the specified instance or fallback to first
      const audioEl = audioElements[instanceIndex] || audioElements[0];
      
      if (!audioEl) {
        const error = new Error(`Audio element instance ${instanceIndex} with class '${className}' not found`);
        console.warn(error.message);
        reject(error);
        return;
      }

      // Reset audio to the beginning to support rapid successive plays
      audioEl.currentTime = 0;

      // Attempt to play and handle promise rejection
      const playPromise = audioEl.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            resolve();
          })
          .catch(error => {
            // Common reason: browser autoplay policy
            console.error(`Failed to play sound '${className}':`, error.message);
            reject(error);
          });
      } else {
        // Older browsers or audio already playing
        resolve();
      }
    } catch (error) {
      console.error(`Error in playSoundByClass:`, error);
      reject(error);
    }
  });
};

/**
 * Creates multiple audio elements for a sound to support simultaneous playback
 * Useful for rapid scanning where multiple sounds may need to play at once
 * @param {string} className - Base class name for the audio elements
 * @param {string} soundUrl - URL to the sound file
 * @param {number} instanceCount - Number of simultaneous instances to create
 */
export const createMultiAudioElements = (className, soundUrl, instanceCount = 1) => {
  const container = document.getElementById('audio-elements-container');
  
  if (!container) {
    console.warn('Audio elements container not found. Create a div with id="audio-elements-container" in your component.');
    return;
  }

  for (let i = 0; i < instanceCount; i++) {
    const audio = document.createElement('audio');
    audio.className = className;
    
    const source = document.createElement('source');
    source.src = soundUrl;
    source.type = 'audio/mpeg';
    
    audio.appendChild(source);
    container.appendChild(audio);
  }
};

/**
 * Plays a sound with a single function, handling errors silently
 * (doesn't throw, just logs)
 * @param {string} className - The class name of the audio element
 */
export const playSoundSilent = (className, instanceIndex = 0) => {
  playSoundByClass(className, instanceIndex)
    .catch(error => {
      // Error already logged in playSoundByClass, this is just to prevent unhandled rejection
    });
};
